import { TrendingUp, TrendingDown, Wallet, Coins, ArrowDownCircle, ArrowUpCircle, Percent } from "lucide-react";
import { useCurrency } from "@/hooks/useUserCurrency";

interface KPICardsProps {
  /** Saldo em aberto hoje (na rua). */
  outstanding: number;
  activeClients: number;
  /** Caixa recebido no período. */
  received: number;
  principal: number;
  interest: number;
  penalty: number;
  /** Lucro real = juros + multas. */
  profit: number;
  /** Dinheiro colocado na rua no período. */
  loaned: number;
  /** Recebido − emprestado. */
  net: number;
  /** Lucro real ÷ saldo médio na rua, em %. */
  profitability: number;
  /** "no mês" ou "no período". */
  periodLabel: string;
}

export function KPICards({
  outstanding,
  activeClients,
  received,
  principal,
  interest,
  penalty,
  profit,
  loaned,
  net,
  profitability,
  periodLabel,
}: KPICardsProps) {
  const { format } = useCurrency();
  const short = (v: number) => format(v, { maximumFractionDigits: 0 });

  const kpis = [
    {
      title: "Na rua agora",
      value: format(outstanding),
      subtitle: `Ainda não recuperado · ${activeClients} cliente${activeClients === 1 ? "" : "s"} ativo${activeClients === 1 ? "" : "s"}`,
      icon: Wallet,
      variant: "primary" as const,
    },
    {
      title: `Recebido ${periodLabel}`,
      value: format(received),
      subtitle: `Capital ${short(principal)} · Juros ${short(interest)} · Multas ${short(penalty)}`,
      icon: ArrowDownCircle,
      variant: "success" as const,
    },
    {
      title: `Lucro real ${periodLabel}`,
      value: format(profit),
      subtitle: "Juros + multas · não inclui capital devolvido",
      icon: Coins,
      variant: "warning" as const,
    },
    {
      title: `Fluxo líquido ${periodLabel}`,
      value: format(net),
      subtitle: "Recebido menos emprestado",
      icon: net >= 0 ? TrendingUp : TrendingDown,
      variant: net >= 0 ? ("success" as const) : ("destructive" as const),
    },
    {
      title: `Emprestado ${periodLabel}`,
      value: format(loaned),
      subtitle: "Dinheiro colocado na rua",
      icon: ArrowUpCircle,
      variant: "destructive" as const,
    },
    {
      title: "Rentabilidade",
      value: `${profitability.toFixed(1)}%`,
      subtitle: `Lucro real ÷ saldo médio na rua ${periodLabel}`,
      icon: Percent,
      variant: "warning" as const,
    },
  ];

  const variantStyles: Record<string, { iconBg: string; iconColor: string; ring: string; topBar: string }> = {
    default: {
      iconBg: "bg-[hsl(var(--primary)/0.15)]",
      iconColor: "text-primary",
      ring: "ring-[hsl(var(--primary)/0.25)]",
      topBar: "from-primary/70 via-primary/30 to-transparent",
    },
    primary: {
      iconBg: "bg-[hsl(var(--primary)/0.15)]",
      iconColor: "text-primary",
      ring: "ring-[hsl(var(--primary)/0.25)]",
      topBar: "from-primary/70 via-primary/30 to-transparent",
    },
    success: {
      iconBg: "bg-[hsl(var(--success)/0.15)]",
      iconColor: "text-success",
      ring: "ring-[hsl(var(--success)/0.25)]",
      topBar: "from-success/70 via-success/30 to-transparent",
    },
    warning: {
      iconBg: "bg-[hsl(var(--warning)/0.15)]",
      iconColor: "text-warning",
      ring: "ring-[hsl(var(--warning)/0.25)]",
      topBar: "from-warning/70 via-warning/30 to-transparent",
    },
    destructive: {
      iconBg: "bg-[hsl(var(--destructive)/0.15)]",
      iconColor: "text-destructive",
      ring: "ring-[hsl(var(--destructive)/0.25)]",
      topBar: "from-destructive/70 via-destructive/30 to-transparent",
    },
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
      {kpis.map((kpi) => {
        const v = variantStyles[kpi.variant];
        return (
          <div
            key={kpi.title}
            className="relative overflow-hidden surface-card surface-hover p-5 lg:p-6 min-w-0"
          >
            <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${v.topBar}`} />
            <div className="flex items-start justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground break-words leading-snug max-w-[70%]">
                {kpi.title}
              </p>
              <div
                className={`shrink-0 grid place-items-center rounded-2xl ring-1 ${v.iconBg} ${v.iconColor} ${v.ring}`}
                style={{ width: 42, height: 42 }}
              >
                <kpi.icon className="h-[20px] w-[20px]" strokeWidth={2.25} />
              </div>
            </div>
            <p className="mt-4 metric-number text-[28px] lg:text-[30px] leading-tight text-foreground break-words">
              {kpi.value}
            </p>
            <p className="mt-2 text-xs text-muted-foreground leading-snug">
              {kpi.subtitle}
            </p>
          </div>
        );
      })}
    </div>
  );
}
